import { searchPromotedEvents } from '@eventbrite/ads';
import { isEmpty } from 'lodash';
import { DateType } from '../constants/dates';
import { computeLocationInfo } from '../pages/search/utils/tracking';
import {
    OrganicEventSearchApiPayload,
    OrganicEventSearchApiResponse,
    OrganicEventSearchFiltersApiPayload,
    searchOrganicEvents,
} from './searchOrganicEvents';

export type EventSearchInterfaceName =
    | 'home_category'
    | 'homepage'
    | 'search'
    | 'home_category_nightlife';

type AllEventsSearchPayload = OrganicEventSearchApiPayload & {
    /**
     * Name of the frontend surface that is requesting the promoted events.
     */
    interfaceName: EventSearchInterfaceName;
    /**
     * Slots: number of promoted events to retrieve from backend.
     */
    slots: number;
};

export async function searchAllEvents(
    search: AllEventsSearchPayload,
): Promise<OrganicEventSearchApiResponse> {
    // eslint-disable-next-line
    const { interfaceName, slots, ...organicSearch } = search;

    // TODO: remove this workaround when server data query came without this field from SSR flow.
    (search as any).event_search.include_promoted_events_for = undefined;

    // TODO: Currently, search_service is receiving a deprecated flag.
    // We are unsure where this flag is coming for, so we are including this delete to check if errors decrease
    // Remove when investigation ends.
    // https://eventbrite.atlassian.net/browse/EB-207789
    delete (search as any).event_search.include_promoted_events;

    const [organicEventsSearchResponse, promotedEventsSearchResponse] =
        await Promise.allSettled([
            searchOrganicEvents(organicSearch),
            searchPromotedEvents(
                transformToPromotedEventSearchApiPayload(search),
            ),
        ]);

    if (organicEventsSearchResponse.status === 'rejected') {
        if (organicEventsSearchResponse.reason instanceof Error) {
            throw organicEventsSearchResponse.reason;
        } else {
            throw new Error(JSON.stringify(organicEventsSearchResponse.reason));
        }
    }

    if (promotedEventsSearchResponse.status === 'fulfilled') {
        organicEventsSearchResponse.value.events.promoted_results =
            promotedEventsSearchResponse.value;
    }

    return organicEventsSearchResponse.value;
}

export function transformToPromotedEventSearchApiPayload(
    search: Pick<
        AllEventsSearchPayload,
        'event_search' | 'slots' | 'interfaceName'
    >,
) {
    const { event_search, slots, interfaceName } = search;
    const { latitude, longitude, place_id } = computeLocationInfo(event_search);
    const tags =
        event_search.tags &&
        event_search.tags.length > 0 &&
        !isEmpty(event_search.tags[0])
            ? event_search.tags
            : undefined;

    return {
        slots,
        page: event_search.page || 1,
        interface: interfaceName,
        search: {
            dates: transformToDates(event_search.dates),
            date_range: event_search.date_range,
            q: event_search.q,
            price: event_search.price,
            languages: event_search.languages,
            tags: tags,
            place: {
                is_online: event_search.online_events_only || false,
                place_id,
                geo_point:
                    longitude && latitude && !event_search.online_events_only
                        ? {
                              lon: Number(longitude),
                              lat: Number(latitude),
                              radius: 50,
                          }
                        : undefined,
            },
            start_time_range: transformHourRange(event_search.start_hour_range),
        },
    };
}

function transformToDates(dates?: DateType | DateType[]): DateType[] {
    if (!dates) {
        return ['current_future'];
    }
    // TODO: remove this workaround, here per retro-compatibility
    if (typeof dates === 'string') {
        return [dates];
    }
    return dates;
}

function transformHourRange(
    hourRange: OrganicEventSearchFiltersApiPayload['start_hour_range'],
) {
    if (!hourRange) {
        return undefined;
    }

    return {
        from: hourRange.start_hour,
        to: hourRange.end_hour,
    };
}
